/**
 * Live-tunable constants for the run-scene motion stages
 * (tasks/todo.md). One numeric constant per stage lands here, never
 * inline in scene3d.ts/environment.ts, so the whole set can be edited
 * at runtime from one Tweakpane panel (`?tune=1`, wired in main.ts) and
 * dumped back as JSON via its "Copy config" button.
 *
 * FOUNDER PROTOCOL (2026-09-05, tasks/todo.md's stage protocol): these
 * numbers ship with a first-guess default and are NEVER iterated on by
 * reasoning about them -- the founder drags the sliders, copies the
 * JSON, and that JSON gets baked in here verbatim, with no logic change
 * and no adjustment of the values sent.
 *
 * BACKDROP PARALLAX REVERTED (founder, 2026-09-05): `nearParallaxRate`/
 * `midParallaxRate`/`farParallaxRate` and environment.ts's whole
 * scroll-tier mechanism are gone, not just paused. 5.25 units of lateral
 * offset on plates at distance 150-270 subtends about 2 degrees over a
 * 5s clip -- below perception by construction, at any rate value -- and
 * what IS visible reads as the background strafing sideways, not the
 * world moving forward. Stage 1's motion cue is ground UV scroll + pier
 * recycle + fog, below.
 */

// MIRROR -- source of truth is scene3d.ts's PIER_WRAP_FLOOR and the
// deck-material far-edge-fade zone (documented there as 228-240,
// "solid" by 240). Duplicated here (not imported) to avoid a circular
// import -- scene3d.ts already imports `tuning` from this file. Used
// only by the panel's live readout below; if either constant moves in
// scene3d.ts, update it here too.
const PIER_WRAP_FLOOR_MIRROR = -15;
const DECK_FADE_END_MIRROR = 240;

export interface RunSceneTuning {
  /** Deck albedo/normal texture V-offset, in TILE REPEATS, per world-unit
   * of run distance. The deck geometry itself never moves (the squad
   * holds its ground, per scene3d.ts's own documented design -- see
   * STRIDE_M's comment); this is what fakes the ground moving under it.
   * Default 1/3 = one tile-repeat (deckAlbedo.repeat.y's own unit, ~3
   * world units per repeat per that texture's construction comment) per
   * 3 world-units travelled -- i.e. the surface APPEARS to move at the
   * same rate the sim says the world is moving, not an arbitrary fraction
   * of it. */
  groundScrollRate: number;
  /** World-Z spacing between recycled causeway piers (scene3d.ts's
   * `pierMesh`) as they cycle from ahead of the squad to behind it. */
  propSpawnDistance: number;
  /** How many piers (each side, so 2x this many instances) are live at
   * once -- raising this alongside a smaller `propSpawnDistance` is how
   * to get more piers passing close to the camera, where they actually
   * read as motion (see PIER_MAX_COUNT's comment in scene3d.ts for why
   * this is capped rather than unbounded). */
  pierCount: number;
  /** FogExp2 density, `scene.fog` in scene3d.ts. Default 0 (founder,
   * 2026-09-05: "the fade quality is poor -- FogExp2 washes the corridor
   * grey rather than reading as depth, and it does not sit right against
   * the backdrop plates"). The 0.006 first guess (solves ~50%
   * transmittance at world-Z 150) is REJECTED, not just untried --
   * wiring and the slider stay so the founder can keep exploring it, but
   * it ships off. Depth for now comes from the deck's existing far-edge
   * fade; atmospheric depth via fog is deferred to its own later stage,
   * not re-attempted here. */
  fogDensity: number;

  // ---------------------------------------------------------- camera
  // Exposed 2026-09-05, PURE READ-THROUGH of scene3d.ts's own
  // CAM_HEIGHT/CAM_BACK/CAM_LOOKAT_FORWARD/CAM_FOV constants -- defaults
  // below are those exact numbers, so leaving every camera slider alone
  // reproduces preview B pixel-for-pixel. Only the three LIVE per-frame
  // usage sites (dynBack/dynHeight/lookAtZ) and camera.fov were switched
  // to read these; the one-time backdrop-baseline call and the
  // `baselineScreenY` diagnostic helper still read the original module
  // constants (deliberately -- one is a construction-time-only pose that
  // live tracking supersedes every frame after the first, the other is a
  // frozen reference pose for a debug tool, not a rendering path).
  /** Camera eye height above the deck, world units. */
  camHeight: number;
  /** Camera distance behind the squad's forward position, world units. */
  camBack: number;
  /** How far ahead of the squad the camera looks, world units. */
  camLookAtForward: number;
  /** Vertical FOV, degrees. */
  camFov: number;
  /** Max additional camBack pull-back as squad size grows toward
   * CAM_SQUAD_PULL_REF (scene3d.ts) -- was the fixed CAM_SQUAD_PULL_MAX
   * constant (1.8), same default here. */
  dynBackMax: number;

  // ---------------------------------------------------------- effects
  /** Multiplies the ability-aura (shield bubble, `s.shieldHp`,
   * scene3d.ts) opacity. This is the only FILLED-VOLUME ability effect
   * in the file -- every other ability visual is a thin ring or a
   * one-shot particle burst -- so it's the leading candidate for
   * "washes out the deck and hides the squad." 1.0 reproduces today's
   * opacity exactly. */
  auraOpacityMult: number;
  /** Multiplies the same aura's radius. 1.0 reproduces today's size. */
  auraRadiusMult: number;
  /** Multiplies the contact-hit flash sprite's size (`spawnHitFlash`,
   * scene3d.ts). 1.0 reproduces today's size. */
  impactFlashScale: number;
  /** Overrides the contact-hit flash's fixed 0.14s lifetime. Default
   * 0.14 matches the pool's own hardcoded default exactly. */
  impactFlashDuration: number;
  /** Bug A fix (founder, 2026-09-05, corrected same date to split by
   * axis): caps only the LATERAL (render-X, `ox`) tracer displacement of
   * `spiral`/`rocket`/`shell` (scene3d.ts's tracer switch) to `this *
   * the smallest enemy radius in config` -- lateral is the only axis
   * that can cause a ground-plane hit-test mismatch, since the sim's
   * collision test reads render-X/render-Z, never render-Y (height).
   * `sweep`/`slam`/`lob` are HEIGHT-only (`oy`) effects, exempt
   * entirely: capping them cannot fix a lateral mismatch and destroys
   * each weapon's whole read (sweep's rising arc, slam's drop, lob's
   * mortar arc). 0 = the three lateral shapes sit exactly on sim
   * position; 1 = each shape's own original amplitude, for whichever
   * was already <= the cap at that setting. Default 0.5. */
  tracerLateralOffsetMult: number;
}

export function defaultTuning(): RunSceneTuning {
  return {
    groundScrollRate: 0.3333333333333333,
    propSpawnDistance: 33,
    // FOUNDER'S CONFIG, BAKED VERBATIM (2026-09-05) -- no value here was
    // adjusted from what was sent.
    //
    // FLAGGED, NOT CHANGED (per instruction: "record the finding," don't
    // fix the numbers): at pierCount=20, propSpawnDistance=33, the piers
    // span PIER_WRAP_FLOOR (-15, scene3d.ts) to -15+20*33=645 world
    // units, while the deck's own far-edge fade completes at 240
    // (documented 228-240). Evenly spaced at 33-unit intervals within
    // that band, only ceil((240-(-15))/33)=8 of the 20 land below
    // Z=240 -- the other 12 are beyond the point the deck material
    // itself has already faded to nothing, i.e. invisible regardless of
    // pierCount. Raising pierCount past ~8 at this spacing adds nothing
    // on screen; it only costs idle InstancedMesh capacity. (The
    // founder's own back-of-envelope used the pre-stutter-fix anchor
    // PIER_Z_START=15 -- far edge 675, "roughly 12 beyond visible
    // range" -- the corrected anchor after moving the wrap floor to -15
    // gives a slightly different far edge (645) but the SAME
    // conclusion: ~12 of 20 beyond the fade, ~8 useful.)
    pierCount: 20,
    fogDensity: 0,

    // FOUNDER'S CONFIG, BAKED VERBATIM (2026-09-06, fourth round) -- no
    // value here was adjusted from what was sent, EXCEPT
    // impactFlashDuration (see its own comment below, an explicit
    // founder amendment to the second-round bake, not my own
    // correction, carried forward unchanged since). Only
    // camLookAtForward (9 -> 5.2) changed from the third round.
    camHeight: 11.5,
    camBack: 8.2,
    camLookAtForward: 5.2,
    // Still below the 72.7 the backdrop plates were sized at (see
    // environment.ts's `sizeBackdropPlate` / the resize-on-live-FOV-
    // change gap flagged 2026-09-05) -- the plate-coverage margin stays
    // safe at this value, not fixed, per instruction.
    camFov: 68.7,
    dynBackMax: 0.6,

    auraOpacityMult: 0.63,
    auraRadiusMult: 0.72,
    impactFlashScale: 1.43,
    // AMENDED BY THE FOUNDER BEFORE BAKING (2026-09-05): sent as 0.34,
    // reverted to the original 0.14 in the same message. At 60fps with
    // overlapping impacts at squad 20, individual flash lifetime was not
    // visually distinguishable at 0.34 -- flashes stacked into a
    // persistent smear instead of reading as discrete hits. Tuned blind;
    // reverted rather than kept. Slider removed from the panel for the
    // same reason (see installTuningPanel) -- not a knob a human can
    // judge by eye.
    impactFlashDuration: 0.14,
    // NOT ROUNDED, per instruction.
    tracerLateralOffsetMult: 0.47000000000000003,
  };
}

/**
 * SKIPPED, NOT FAKED (founder, 2026-09-05, "say so and skip it rather
 * than faking it"):
 *
 * - Enemy spawn density / formation spacing. Spawn counts are authored
 *   per-wave in config/balance/waves.csv (`count` column), loaded into
 *   the sim's config at build time -- there is no render- or spawn-path
 *   runtime multiplier to expose. Enemies also have no client-side
 *   "formation" layer the way the squad does (FILE_GAP/RANK_GAP,
 *   scene3d.ts) -- each has its own independent sim position (`s.eX`/
 *   `s.eY`), so there is no separate spacing constant either. Wiring a
 *   live multiplier into either would mean scaling actual sim behavior
 *   from a client-only slider, which breaks Iron Rule 1 (a run replays
 *   from config+seed+inputLog alone) the moment it's used for anything
 *   beyond this dev session.
 * - Enemy hit-flash COLOR INTENSITY. `enemyFlash` (scene3d.ts) is a
 *   per-enemy frame counter, written on hit (main.ts) and decremented
 *   every frame (scene3d.ts) -- but its value is never read anywhere to
 *   affect a body's color, emissive, or brightness. The mechanism the
 *   comment beside it describes ("painted the body white for a few
 *   frames") was superseded by the richer position/rotation/scale/pose
 *   hit-reaction system documented right above it and is now dead:
 *   built, then neutralised (check-before-building category 3), not a
 *   tunable to expose. A slider here would move a number nothing reads.
 */

/** ONE mutable instance, shared by reference between the Tweakpane panel
 * (main.ts, `?tune=1` only) and the render loops that read it every
 * frame (scene3d.ts, environment.ts). Never replaced, only mutated --
 * every reader holds this same reference from module load. */
export const tuning: RunSceneTuning = defaultTuning();

/** How many of `tuning.pierCount` piers currently sit below the deck's
 * far-edge-fade completion (Z<240), independent of animation phase
 * (evenly-spaced points in a wrapping band -- the count within a fixed
 * sub-range only varies by at most 1 as the band scrolls). Exported for
 * the panel's live readout, below. */
function piersWithinFade(): number {
  const span = tuning.pierCount * tuning.propSpawnDistance;
  if (span <= 0) return 0;
  const inRange = Math.ceil((DECK_FADE_END_MIRROR - PIER_WRAP_FLOOR_MIRROR) / tuning.propSpawnDistance);
  return Math.max(0, Math.min(tuning.pierCount, inRange));
}

/**
 * Builds the live tuning panel. Dynamically imports `tweakpane` so the
 * dependency never enters the shipping bundle unless `?tune=1` is
 * actually present (bundlesize budget, CLAUDE.md: SHELL is what the
 * player waits for). Dev-only; ships inert without the query param.
 */
export async function installTuningPanel(): Promise<void> {
  const { Pane } = await import('tweakpane');
  // DOCKED + COLLAPSED BY DEFAULT (founder, 2026-09-05: "if the panel
  // covers the game canvas on a phone, make it collapsible or dock it
  // to a corner"). Tweakpane's default container is an undocked
  // fixed-width block that, at a 390px viewport, covers most of the
  // canvas -- this container caps its width to a fraction of the
  // viewport instead and pins it to the top-right corner; `expanded:
  // false` starts it collapsed to just the title bar so the scene is
  // fully visible until the founder taps it open.
  const container = document.createElement('div');
  container.style.cssText = 'position:fixed;top:0;right:0;z-index:9999;max-width:70vw;';
  document.body.appendChild(container);
  const pane = new Pane({ title: 'Run-scene tuning', container, expanded: false });
  pane.addBinding(tuning, 'groundScrollRate', { min: 0, max: 1, step: 0.005 });
  // Range narrowed (founder, 2026-09-05): above ~40, EVERY pier at the
  // default count sits past the deck's far-edge fade (see
  // `piersWithinFade` above) -- the slider used to let the founder drag
  // into a region that provably cannot do anything on screen.
  pane.addBinding(tuning, 'propSpawnDistance', { min: 4, max: 40, step: 1 });
  // PIER_MAX_COUNT (scene3d.ts) is 20 -- this slider's max must not
  // exceed the InstancedMesh capacity allocated there.
  pane.addBinding(tuning, 'pierCount', { min: 2, max: 20, step: 1 });
  // Range spans plainly-off (0) to plainly-heavy: 0.04 solves
  // exp(-(density*50)^2)=0.05, i.e. ~95% opacity by world-Z 50 -- thick
  // enough to read as a wall at close range, the upper end of "heavy."
  pane.addBinding(tuning, 'fogDensity', { min: 0, max: 0.04, step: 0.0005 });

  const camFolder = pane.addFolder({ title: 'Camera', expanded: false });
  camFolder.addBinding(tuning, 'camHeight', { min: 4, max: 20, step: 0.1 });
  camFolder.addBinding(tuning, 'camBack', { min: 2, max: 16, step: 0.1 });
  camFolder.addBinding(tuning, 'camLookAtForward', { min: 0, max: 12, step: 0.1 });
  camFolder.addBinding(tuning, 'camFov', { min: 30, max: 100, step: 0.5 });
  camFolder.addBinding(tuning, 'dynBackMax', { min: 0, max: 8, step: 0.1 });

  const fxFolder = pane.addFolder({ title: 'Effects', expanded: false });
  fxFolder.addBinding(tuning, 'auraOpacityMult', { min: 0, max: 2, step: 0.01 });
  fxFolder.addBinding(tuning, 'auraRadiusMult', { min: 0, max: 2, step: 0.01 });
  fxFolder.addBinding(tuning, 'impactFlashScale', { min: 0, max: 3, step: 0.01 });
  // NO SLIDER (founder, 2026-09-05): impactFlashDuration was tuned blind
  // to 0.34, produced a persistent smear at squad-20 overlap rather than
  // discrete hits, and was reverted to 0.14. Not a knob a human can
  // judge by eye at 60fps -- the field stays and the render loop still
  // reads it live (scene3d.ts), but dragging it invites the same blind
  // adjustment again, so no UI control is offered.
  fxFolder.addBinding(tuning, 'tracerLateralOffsetMult', { min: 0, max: 1, step: 0.01 });

  // LIVE READOUT (founder, 2026-09-05: "the effect of both sliders
  // visible while dragging instead of guessable"). A plain object with
  // a readonly binding, kept in sync on every panel change and on a
  // slow poll (dragging fires 'change' continuously, so the poll is
  // just a safety net, not the primary update path).
  const readout = { piersInFade: '' };
  function refreshReadout(): void {
    readout.piersInFade = `${piersWithinFade()} / ${tuning.pierCount} within Z<240`;
  }
  refreshReadout();
  const monitor = pane.addBinding(readout, 'piersInFade', { readonly: true, label: 'piers visible', interval: 200 });
  pane.on('change', refreshReadout);
  void monitor;
  pane.addButton({ title: 'Copy config' }).on('click', () => {
    const json = JSON.stringify(tuning, null, 2);
    void navigator.clipboard?.writeText(json).catch(() => {});
    // Fallback/visibility: clipboard write can silently fail (permissions,
    // insecure context) and there is no toast system in this dev-only
    // panel to report success -- the console line is always there either
    // way, copy-pasteable by hand if the clipboard call didn't take.
    console.log('[tune] config:', json);
  });
}
